import { redirect } from '@tanstack/react-router';
import {
  ApiError,
  AuthRequiredError,
  TotpStepUpRequiredError,
  unwrap,
} from '@truecare/api-client';
import { api } from '../api';
import { buildSignInRedirect } from './portal-redirect';

/**
 * Phase 14b-1 post-audit M4/M5 — shared `beforeLoad` for all admin
 * routes. Extracted so the auth-redirect-vs-step-up-rethrow logic
 * lives in ONE place; per-route duplication invited the 2f
 * `instanceof ApiError` over-match bug (the `instanceof ApiError`
 * branch was catching `TotpStepUpRequiredError` as a subclass and
 * redirecting operators to sign-in instead of bubbling the error to
 * the global step-up modal).
 *
 * Behavior:
 *   - GET /auth/me succeeds → routes load normally.
 *   - `AuthRequiredError` (401, no session) → redirect to portal
 *     sign-in URL with `?next=<currentAdminPath>`.
 *   - `TotpStepUpRequiredError` (401, reason: 'totp_step_up_required')
 *     → rethrow so the global `MutationCache` `onError` can open the
 *     step-up modal + retry. At v1 no GET is `TotpFreshGuard`-gated so
 *     this branch is dormant for `/auth/me`; the guard exists so that
 *     a future engineer who gates a GET doesn't accidentally regress
 *     the auth flow.
 *   - Other `ApiError` (4xx/5xx) → treat as auth failure; redirect to
 *     sign-in. This is the conservative pre-flight: any error reading
 *     `/auth/me` means we can't trust the session.
 *   - Non-`ApiError` errors → rethrow (router renders an error
 *     boundary).
 *
 * Add the shared spec in `admin-before-load.spec.ts` if changing this
 * function — it's the single load-bearing auth gate for admin pages.
 */
export async function adminBeforeLoad(currentPath: string): Promise<void> {
  let me: {
    totpEnrolled?: boolean;
    totpSatisfied?: boolean;
    roles?: readonly { persona: string }[];
  };
  try {
    me = await unwrap(api.GET('/api/v1/auth/me'));
  } catch (err) {
    // Step-up errors MUST bubble — they're the global modal's signal,
    // not an auth failure. Check BEFORE the generic ApiError branch
    // because TotpStepUpRequiredError extends ApiError.
    if (err instanceof TotpStepUpRequiredError) {
      throw err;
    }
    if (err instanceof AuthRequiredError || err instanceof ApiError) {
      if (typeof window !== 'undefined') {
        window.location.href = buildSignInRedirect(currentPath);
      }
      throw redirect({ to: '/sign-in-required' });
    }
    throw err;
  }

  // The admin app is OPERATOR-ONLY. A signed-in NON-operator (e.g. a caregiver
  // who followed a stale ?next=<admin> or typed the admin URL) doesn't belong
  // here — send them to the portal (their persona home is portal /me), NOT the
  // operator 2FA gate and NOT a wall of 403s. Previously adminBeforeLoad only
  // checked that /auth/me succeeded, so any authenticated user passed through.
  const isOperator = (me.roles ?? []).some((r) => r.persona === 'OPERATOR');
  if (!isOperator) {
    if (typeof window !== 'undefined') {
      const portalUrl =
        (import.meta.env['VITE_PORTAL_URL'] as string | undefined) ||
        (import.meta.env['PUBLIC_PORTAL_URL'] as string | undefined);
      window.location.href = portalUrl || buildSignInRedirect(currentPath);
    }
    throw redirect({ to: '/sign-in-required' });
  }

  // Operators must have 2FA enrolled (every /admin/* endpoint is OperatorGuard-
  // gated on enrolled + satisfied). A fresh operator without TOTP would otherwise
  // hit "Failed to load" everywhere — route them to the 2FA setup gate instead.
  // The gate passes its own path so this doesn't loop, and sends them home once
  // enrolled.
  if (me.totpEnrolled === false && currentPath !== '/setup-2fa') {
    throw redirect({ to: '/setup-2fa' });
  }

  // Enrolled, but the CURRENT session has not SATISFIED TOTP (totpSatisfiedAt
  // null) — e.g. signed in on the portal but closed the tab before entering the
  // code, or a freshly-minted post-email-change / post-reset session. It passes
  // the enrolled check above, so previously it was admitted and then every
  // /admin/* query 403'd (reason: totp_verification_required) — a dead-end with
  // no in-app recovery (admin has no TOTP-verify page; on401 is 401-only; the
  // step-up modal is 401/totp_step_up_required-only). Bounce to the portal's
  // /sign-in/totp to satisfy the second factor on the EXISTING session, then
  // ?next= returns here. 2026-10-18 auth audit (MED). /auth/me already exposes
  // totpSatisfied, so the guard has the data to detect this.
  if (me.totpEnrolled === true && me.totpSatisfied === false) {
    if (typeof window !== 'undefined') {
      window.location.href = buildSignInRedirect(currentPath, '/sign-in/totp');
    }
    throw redirect({ to: '/sign-in-required' });
  }
}
